import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import DatePicker from "react-multi-date-picker";
import { Dialog, Button, Dropdown, TextInput, Image } from "hesus-ui-kit";
import * as moment from "moment";
import {
  getDatePickerFormat,
  getDateFormat,
  getCalendarLocale,
} from "@utils/datePickerUtils";
import ClipLoader from "react-spinners/ClipLoader";
import PulseLoader from "react-spinners/PulseLoader";
import { handleApiError } from "@utils/errorHandler";
import { SolutionAcceptanceUnit } from "@utils/constants";
import DocumentDragDrop from "@components/DocumentDragDrop/DocumentDragDrop";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
  RequiredMessagePara,
} from "@utils/extra-style";
import UserInfo from "@utils/UserInfo";
import {
  HeaderWrapper,
  Title,
  ButtonWrapper,
  FormBody,
  OptionsWrapper,
  FlexRow,
  SectionContainer,
  GridRow,
  Labeltext,
  LabeltextNotRequired,
} from "./AddEditSolutionAcceptance.styled";
import "./AddEditSolutionAcceptance.scss";
import closeIcon from "@icons/ic-close-white.svg";

const AddEditSolutionAcceptance = (props) => {
  const {
    t,
    id,
    solutionId,
    isEditMode,
    onClose,
    updateRefeshStatus,
    supplierName,
    centreName,
    isAddSolutionAcceptanceInPopUp,
  } = props;
  const [isAcceptanceCreating, setIsAcceptanceCreating] = useState(false);
  const [createAcceptanceAPIError, setCreateAcceptanceAPIError] = useState("");
  const [acceptanceData, setAcceptanceData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]);

  const userDetails = UserInfo.getUser();

  const [formData, setFormData] = useState({
    number: {
      required: true,
      value: "",
      error: "",
    },
    startDate: {
      required: true,
      value: "",
      error: "",
    },
    endDate: {
      required: true,
      value: "",
      error: "",
    },
    quantity: {
      required: true,
      value: "",
      error: "",
    },
    unit: {
      required: true,
      value: "",
      error: "",
    },
    comment: {
      required: true,
      value: "",
      error: "",
    },
    documents: [],
  });

  const fetchAcceptanceData = (solutionId, solutionAcceptanceId) => {
    setIsLoading(true);
    axios
      .get(`/solutions/${solutionId}/acceptances/${solutionAcceptanceId}`)
      .then((response) => {
        if (response.data?.[0]) {
          setAcceptanceData(response.data?.[0]);
          setIsLoading(false);
        }
      })
      .catch((error) => handleApiError(error));
  };

  useEffect(() => {
    if (isEditMode) fetchAcceptanceData(solutionId, id);
  }, [isEditMode]);

  useEffect(() => {
    if (isEditMode && acceptanceData?.id) {
      const formUpdatedInfo = {
        number: {
          required: true,
          value: acceptanceData?.number ?? "",
          error: "",
        },
        startDate: {
          required: true,
          value:
            moment(acceptanceData?.validityStart).format("YYYY-MM-DD") ?? "",
          error: "",
        },
        endDate: {
          required: true,
          value: moment(acceptanceData?.validityEnd).format("YYYY-MM-DD") ?? "",
          error: "",
        },
        quantity: {
          required: true,
          value: acceptanceData?.capacityQuantity ?? "",
          error: "",
        },
        unit: {
          required: true,
          value: acceptanceData?.capacityUnit ?? "",
          error: "",
        },
        comment: {
          required: true,
          value: acceptanceData?.comment ?? "",
          error: "",
        },
        documents: [],
      };
      setFormData(formUpdatedInfo);
      setIsLoading(false);
    }
  }, [isEditMode, acceptanceData]);

  const onNumberChange = (e) => {
    setFormData({
      ...formData,
      number: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onStartDateChange = (e) => {
    setFormData({
      ...formData,
      startDate: {
        value: moment(e.toDate()).format("YYYY-MM-DD"),
        error: "",
        required: true,
      },
    });
  };

  const onEndDateChange = (e) => {
    setFormData({
      ...formData,
      endDate: {
        value: moment(e.toDate()).format("YYYY-MM-DD"),
        error: "",
        required: true,
      },
    });
  };

  const onQuantityChange = (e) => {
    setFormData({
      ...formData,
      quantity: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onUnitChange = (e) => {
    setFormData({
      ...formData,
      unit: {
        value: e.value,
        error: "",
        required: true,
      },
    });
  };

  const onCommentChange = (e) => {
    setFormData({
      ...formData,
      comment: {
        value: e.target.value,
        error: "",
        required: false,
      },
    });
  };

  const onDocumentUpload = (documentArray) => {
    setSelectedFiles([...documentArray]);
    // setFormData({
    //   ...formData,
    //   documents: [...documentArray],
    // });
  };
  useEffect(() => {
    setFormData({
      ...formData,
      documents: [...selectedFiles],
    });
  }, [selectedFiles]);

  //   useEffect(() => {
  //     if (selectedProduct && isEditMode) {
  //       setExistingDocuments(selectedProduct.documents);
  //     }
  //   }, [acceptanceData?.documents, isEditMode]);

  useEffect(() => {
    if (acceptanceData && isEditMode) {
      setExistingDocuments(acceptanceData.documents);
    }
  }, [acceptanceData, isEditMode]);

  const validateForm = (data) => {
    let isFormValid = true;
    if (!data.number.value) {
      isFormValid = false;
      data.number.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.startDate.value) {
      isFormValid = false;
      data.startDate.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.endDate.value) {
      isFormValid = false;
      data.endDate.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!isFormValid) {
      setFormData({ ...data });
    }

    return isFormValid;
  };

  const getFormData = () => {
    const dataModified = new FormData();
    dataModified.append("id", isEditMode ? acceptanceData.id : "");
    dataModified.append("number", formData.number.value);
    dataModified.append("validityStart", formData.startDate.value);
    dataModified.append("validityEnd", formData.endDate.value);
    dataModified.append("capacityQuantity", formData.quantity.value);
    dataModified.append("capacityUnit", formData.unit.value);
    dataModified.append("comment", formData.comment.value);
    dataModified.append(
      "createdBy",
      JSON.stringify({
        id: userDetails.id,
      })
    );
    dataModified.append(
      "updatedBy",
      JSON.stringify({
        id: userDetails.id,
      })
    );

    if (formData.documents.length > 0) {
      for (let i = 0; i < formData.documents.length; i++) {
        dataModified.append(`documents[${i}][document][type]`, 17);
        dataModified.append(
          `documents[${i}][document][filename]`,
          formData.documents[i].name
        );
        dataModified.append(
          `documents[${i}][document][file]`,
          formData.documents[i]
        );
      }
    }
    if (existingDocuments.length > 0) {
      let i = formData.documents.length > 0 ? formData.documents.length : 0;
      existingDocuments.forEach((document) => {
        dataModified.append(
          `documents[${i}][document][filename]`,
          document.fileName
        );
        dataModified.append(`documents[${i}][document][id]`, document.id);
        dataModified.append(
          `documents[${i}][document][file]`,
          document.fileName
        );
        dataModified.append(`documents[${i}][document][type]`, 17);
        i += 1;
      });
    }

    return dataModified;
  };

  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (isFormValid) {
      setIsAcceptanceCreating(true);
      setCreateAcceptanceAPIError("");
      // Create solution
      if (!id && !isEditMode) {
        axios
          .post(`/solutions/${solutionId}/acceptances`, getFormData())
          .then(() => {
            setIsAcceptanceCreating(false);
            if (!isAddSolutionAcceptanceInPopUp) updateRefeshStatus(true);
            onClose();
          })
          .catch((error) => {
            setIsAcceptanceCreating(false);
            setCreateAcceptanceAPIError(error.response?.data?.message ?? "");
          });
      }
      if (id && isEditMode) {
        axios
          .post(`/solutions/${solutionId}/acceptances/${id}`, getFormData())
          .then(() => {
            setIsAcceptanceCreating(false);
            // updateRefeshStatus();
            onClose();
          })
          .catch((error) => {
            setIsAcceptanceCreating(false);
            setCreateAcceptanceAPIError(error.response?.data?.message ?? "");
          });
      }
    }
  };

  return (
    <Dialog>
      <div className="add-edit-acceptance">
        <HeaderWrapper>
          <Title>
            {t("SOLUTION_NEW_ACCEPTANCE_TITLE") +
              " - [" +
              supplierName +
              "] " +
              centreName}
          </Title>
          <Button
            onClick={onClose}
            styles={{
              backgroundColor: "transparent",
              border: "none",
              height: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Image
              src={closeIcon}
              alt="close transport popup"
              styles={{ width: "auto" }}
            />
          </Button>
        </HeaderWrapper>
        <FormBody>
          <OptionsWrapper>
            <FlexRow>
              <SectionContainer>
                {isLoading && (
                  <div className="suggestions-list-loading">
                    <PulseLoader color="#1bace4" size={15} />
                  </div>
                )}
                {!isLoading && (
                  <div>
                    <GridRow column="1fr 1fr">
                      <TextInput
                        id="label"
                        type="text"
                        label={t("SOLUTION_ACCEPTANCE_NUMBER")}
                        isFloat
                        required
                        value={String(formData.number.value)}
                        onChange={(e) => onNumberChange(e)}
                        containerStyles={{ marginTop: "21px" }}
                        errorStyles={{ "font-style": "italic" }}
                        labelStyles={
                          formData.number.error !== ""
                            ? labelStylesError
                            : labelStyles
                        }
                        inputStyles={
                          formData.number.error !== ""
                            ? inputStylesError
                            : inputStyles
                        }
                        error={formData.number.error}
                        placeholder={t("SOLUTION_ACCEPTANCE_NUMBER")}
                      />
                    </GridRow>
                    <GridRow column="1fr 1fr" gap="5.50rem">
                      <div
                        className={
                          formData.startDate.error
                            ? "add-edit-acceptance__date-container_start_error"
                            : "add-edit-acceptance__date-container_start add-edit-acceptance__date-container_start_required"
                        }
                      >
                        <div>
                          <Labeltext htmlFor="start-date">
                            {t("SOLUTION_ACCEPTANCE_DATES")}
                          </Labeltext>
                        </div>
                      </div>
                      <div>
                        <div>
                          <LabeltextNotRequired htmlFor="start-date">
                            {t("SOLUTION_ACCEPTANCE_CAPACITY")}
                          </LabeltextNotRequired>
                        </div>
                      </div>
                    </GridRow>
                    <GridRow column="1fr 1fr">
                      <GridRow column="1fr 1fr">
                        <div
                          className={
                            formData.startDate.error
                              ? "add-edit-acceptance__date-container_start_error"
                              : "add-edit-acceptance__date-container_start add-edit-acceptance__date-container_start_required"
                          }
                        >
                          <DatePicker
                            inputClass={
                              formData.startDate.error
                                ? "add-edit-acceptance__date-input-error"
                                : "add-edit-acceptance__date-input"
                            }
                            containerClassName="add-edit-acceptance__datepicker-container"
                            type="text"
                            label=""
                            name="add-edit-acceptance-start-date"
                            id="add-edit-acceptance-start-date"
                            calendarPosition="bottom-center"
                            isFloat
                            onChange={onStartDateChange}
                            format={getDatePickerFormat()}
                            locale={getCalendarLocale()}
                            value={
                              getDateFormat(
                                formData.startDate.value,
                                "YYYY-MM-DD"
                              ) || ""
                            }
                            containerStyles={{ marginTop: "21px" }}
                            labelStyles={
                              formData.startDate.error !== ""
                                ? labelStylesError
                                : labelStyles
                            }
                            required
                            error={formData.startDate.error}
                            placeholder=""
                          />
                          {formData.startDate.error && (
                            <RequiredMessagePara>
                              {formData.startDate.error}
                            </RequiredMessagePara>
                          )}
                        </div>
                        <div className="add-edit-acceptance__date-container_start ">
                          <div>
                            {/* <label htmlFor="winning-date">
                            
                          </label> */}
                          </div>
                          <DatePicker
                            inputClass={
                              formData.startDate.error
                                ? "add-edit-acceptance__date-input-error"
                                : "add-edit-acceptance__date-input"
                            }
                            containerClassName="add-edit-acceptance__datepicker-container"
                            type="text"
                            label={false}
                            name="add-edit-acceptance-end-date"
                            id="add-edit-acceptance-end-date"
                            calendarPosition="bottom-center"
                            isFloat
                            onChange={onEndDateChange}
                            format={getDatePickerFormat()}
                            locale={getCalendarLocale()}
                            value={
                              getDateFormat(
                                formData.endDate.value,
                                "YYYY-MM-DD"
                              ) || ""
                            }
                            labelStyles={
                              formData.endDate.error !== ""
                                ? labelStylesError
                                : labelStyles
                            }
                            error={formData.endDate.error}
                            placeholder=""
                          />
                          {formData.endDate.error && (
                            <RequiredMessagePara>
                              {formData.endDate.error}
                            </RequiredMessagePara>
                          )}
                        </div>
                      </GridRow>
                      <GridRow column="1fr 1fr">
                        <TextInput
                          id="label"
                          type="text"
                          label={false}
                          isFloat
                          containerStyles={{ marginTop: "-4px" }}
                          value={String(formData.quantity.value)}
                          onChange={(e) => onQuantityChange(e)}
                          errorStyles={{ "font-style": "italic" }}
                          labelStyles={
                            formData.quantity.error !== ""
                              ? labelStylesError
                              : labelStyles
                          }
                          inputStyles={
                            formData.quantity.error !== ""
                              ? inputStylesError
                              : inputStyles
                          }
                          error={formData.quantity.error}
                          placeholder=""
                        />
                        <Dropdown
                          id="add-edit-acceptance-unit"
                          name="unit"
                          label={false}
                          value={formData.unit.value}
                          onChange={onUnitChange}
                          defaultOption={t("UNIT")}
                          options={SolutionAcceptanceUnit(t)}
                          containerStyles={{ marginTop: "-28px" }}
                          labelStyles={
                            formData.unit.error !== ""
                              ? labelStylesError
                              : labelStyles
                          }
                          inputStyles={
                            formData.unit.error !== ""
                              ? inputStylesError
                              : inputStyles
                          }
                          error={formData.unit.error}
                        />
                      </GridRow>
                    </GridRow>
                    <GridRow column="1fr">
                      <TextInput
                        id="acceptance-comment"
                        type="textarea"
                        label={t("SOLUTION_ACCEPTANCE_COMMENT")}
                        autoComplete="off"
                        isFloat
                        value={String(formData.comment.value)}
                        onChange={onCommentChange}
                        containerStyles={{ marginTop: "21px" }}
                        errorStyles={{ "font-style": "italic" }}
                        labelStyles={
                          formData.comment.error !== ""
                            ? labelStylesError
                            : labelStyles
                        }
                        inputStyles={
                          formData.comment.error !== ""
                            ? inputStylesError
                            : inputStyles
                        }
                        error={formData.comment.error}
                        placeholder={t("SOLUTION_ACCEPTANCE_COMMENT")}
                        rows={6}
                      />
                    </GridRow>
                    <GridRow>
                      <DocumentDragDrop
                        isEditMode={isEditMode}
                        onDocumentUpload={onDocumentUpload}
                        documentList={formData?.documents}
                        selectedProduct={acceptanceData}
                        existingDocuments={existingDocuments}
                        setExistingDocuments={setExistingDocuments}
                        title={t("SOLUTION_ACCEPTANCE_DOCUMENTS")}
                        description={t("SOLUTION_ACCEPTANCE_DOCUMENTS_LABEL")}
                        width="100%"
                      />
                    </GridRow>
                  </div>
                )}
              </SectionContainer>
            </FlexRow>
          </OptionsWrapper>
        </FormBody>

        <ButtonWrapper>
          {createAcceptanceAPIError && <div>{createAcceptanceAPIError}</div>}
          <Button
            styles={{
              color: "white",
              background: "#1BACE4",
              border: "1px solid #1BACE4",
              marginRight: "0.32rem",
              "&:hover": {
                backgroundColor: "#1589b6",
              },
            }}
            onClick={onSubmit}
          >
            <>
              {isAcceptanceCreating && <ClipLoader size={15} color="#fff" />}
              {!isAcceptanceCreating && t("SUBMIT")}
            </>
          </Button>

          <Button
            styles={{
              color: "#333",
              background: "#fff",
              border: "1px solid #ccc",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={onClose}
          >
            {t("CLOSE")}
          </Button>
        </ButtonWrapper>
      </div>
    </Dialog>
  );
};

AddEditSolutionAcceptance.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.number,
  title: PropTypes.string.isRequired,
  isEditMode: PropTypes.bool,
  isAddSolutionAcceptanceInPopUp: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  updateRefeshStatus: PropTypes.func.isRequired,
};

AddEditSolutionAcceptance.defaultProps = {
  id: null,
  isEditMode: false,
  isAddSolutionAcceptanceInPopUp: false,
};
export default withTranslation()(AddEditSolutionAcceptance);
